import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { CalendarTaskFrom } from '@demind-inc/core';
import { useRecoilState } from 'recoil';

import {
  useAuthContext,
  useTodoTasksCache,
  useTodoProjects,
  visibleTaskDetailAtom,
} from '../../../data-access';

import { KanbanTaskCard } from '../../KanbanView/KanbanTaskCard';
import { getDueDateYYYYMMDD } from '../../../helpers';

import './TodoSuggestedEventSheetContent.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

interface TodoSuggestedEventSheetContentProps {
  startTime: string;
  onClose?: () => void;
  isPredicted?: boolean;
  description: string;
  taskFrom?: CalendarTaskFrom;
  disableInteraction?: boolean;
  onToggleTaskComplete?: (completed: boolean, mutateDate?: string) => void;
}

const TodoSuggestedEventSheetContent: React.FC<TodoSuggestedEventSheetContentProps> = ({
  startTime,
  onClose,
  isPredicted = false,
  description,
  taskFrom,
  disableInteraction = false,
  onToggleTaskComplete,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { findInCacheByDate } = useTodoTasksCache();
  const userId = user?.userId ?? '';
  const { todoProjects } = useTodoProjects({ userId });
  const [, setVisibleTask] = useRecoilState(visibleTaskDetailAtom);

  const startDayjs = useMemo(() => {
    const parsed = dayjs(startTime);
    return parsed.isValid() ? parsed : null;
  }, [startTime]);

  const linkedTask = useMemo(() => {
    if (!taskFrom?.taskId || !taskFrom.boardId || !startDayjs) {
      return undefined;
    }
    const tasks = findInCacheByDate(startDayjs.format('YYYY-MM-DD'), taskFrom.boardId);
    return tasks.find((task) => task.taskId === taskFrom.taskId);
  }, [taskFrom?.taskId, taskFrom?.boardId, startDayjs, findInCacheByDate]);

  const initialTaskCompleted = linkedTask?.completed ?? false;
  const [localTaskCompleted, setLocalTaskCompleted] = useState(initialTaskCompleted);

  // Sync local task completion state with linkedTask
  useEffect(() => {
    if (linkedTask !== undefined) {
      setLocalTaskCompleted(linkedTask.completed ?? false);
    }
  }, [linkedTask]);

  const projectName = useMemo(() => {
    if (!taskFrom?.boardId) {
      return '';
    }
    return todoProjects.find((project) => project.todoTaskId === taskFrom.boardId)?.name ?? '';
  }, [todoProjects, taskFrom?.boardId]);

  const handleTaskClick = () => {
    if (linkedTask) {
      onClose?.();
      setVisibleTask(linkedTask);
    }
  };

  const handleTaskComplete = useCallback(
    (taskId: string, completed: boolean) => {
      if (disableInteraction || !linkedTask || !linkedTask.boardId) {
        return;
      }
      // Optimistic update
      setLocalTaskCompleted(completed);
      const mutateDate = getDueDateYYYYMMDD(linkedTask.dueDateTime) || dayjs().format('YYYY-MM-DD');
      onToggleTaskComplete?.(completed, mutateDate);
    },
    [disableInteraction, linkedTask, onToggleTaskComplete]
  );

  // Create task object with local completion status for optimistic UI updates
  const taskWithLocalCompleted = useMemo(() => {
    if (!linkedTask) {
      return undefined;
    }
    return {
      ...linkedTask,
      completed: localTaskCompleted,
    };
  }, [linkedTask, localTaskCompleted]);

  const contentSections = useMemo(() => {
    const sections: { key: string; node: React.ReactNode }[] = [];

    if (taskWithLocalCompleted) {
      sections.push({
        key: 'task-preview',
        node: (
          <div className="todo-suggested-event-sheet-content__section">
            <Typography className="todo-suggested-event-sheet-content__section-title">
              {t('calendar.suggested.taskPreview')}
            </Typography>
            <KanbanTaskCard
              task={taskWithLocalCompleted}
              projectName={projectName}
              onClick={handleTaskClick}
              onTaskComplete={handleTaskComplete}
              className="todo-suggested-event-sheet-content__task-preview"
            />
          </div>
        ),
      });
    }

    if (isPredicted) {
      sections.push({
        key: 'why-task-time',
        node: (
          <div
            className={clsx(
              'todo-suggested-event-sheet-content__section',
              'todo-suggested-event-sheet-content__section--why-task-time'
            )}
          >
            <Typography className="todo-suggested-event-sheet-content__section-title">
              {t('calendar.suggested.whyTaskTime')}
            </Typography>
            <div
              className="todo-suggested-event-sheet-content__description-container"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        ),
      });
    }

    return sections;
  }, [
    description,
    handleTaskClick,
    handleTaskComplete,
    isPredicted,
    projectName,
    taskWithLocalCompleted,
  ]);

  return (
    <div className="todo-suggested-event-sheet-content">
      {contentSections.map(({ key, node }, index) => (
        <React.Fragment key={key}>
          {index > 0 && (
            <div className="todo-suggested-event-sheet-content__divider" role="separator" />
          )}
          {node}
        </React.Fragment>
      ))}
    </div>
  );
};

export default TodoSuggestedEventSheetContent;
